/**
 * Tipos y llamadas REALES de `consola/verificacion` (`apps/api/src/conductores/verificacion.
 * controller.ts`). Estos tipos NO son los de `dominio/contratos.ts` — son DISTINTOS a propósito.
 *
 * 🔴 **Los mock de `dominio/contratos.ts` (`Conductor`, `Documento`, `EstadoVerificacion`,
 * `EstadoRevisionDocumento`, `TipoDocumento`) NO COINCIDEN con lo que el backend real devuelve.**
 * Medido leyendo `apps/api/prisma/schema.prisma` + verificado por efecto contra un backend real:
 *
 * ```
 *                          MOCK (dominio/contratos.ts)          REAL (apps/api)
 * EstadoVerificacion       SIN_ENVIAR·EN_REVISION·APROBADO·      PENDIENTE·EN_REVISION·APROBADO·
 *                          OBSERVADO·RECHAZADO                   RECHAZADO·SUSPENDIDO
 * EstadoRevisionDocumento  PENDIENTE·APROBADO·OBSERVADO·         PENDIENTE·APROBADO·RECHAZADO
 *                          RECHAZADO                             (sin OBSERVADO — es binario)
 * TipoDocumento            DNI·LICENCIA·SOAT·TARJETA_PROPIEDAD·   LICENCIA_CONDUCIR·SOAT·
 *                          REVISION_TECNICA·ANTECEDENTES          TARJETA_PROPIEDAD·REVISION_TECNICA
 * ```
 *
 * No es un descuido de esta pantalla: los mock los declaró otra ronda ANTES de que existiera
 * `apps/api`, y nadie los reconcilió todavía (es justo lo que el propio encabezado de
 * `contratos.ts` anticipa: "cuando el paquete lo cubra, cada bloque se reemplaza"). Reusar esos
 * tipos acá habría compilado igual — son casi todos `string` bajo el capó — y hubiera mostrado
 * datos reales con las etiquetas EQUIVOCADAS (`Etiqueta`/`TIPO_DOCUMENTO` de
 * `componentes/Etiqueta.tsx` están indexados por los tipos MOCK). Por eso este archivo declara
 * sus propios tipos y su propio diccionario de etiquetas — ver `Verificacion.tsx`.
 *
 * ⚠️ **Backend NO tiene un estado "OBSERVADO"**: rechazar un documento con motivo es la única
 * vía de "esto no sirve, corregilo" que existe hoy — no hay un tercer estado intermedio. La
 * pantalla usa la palabra "Observar" para la acción (mismo copy que ya tenía la pantalla mock,
 * más suave que "Rechazar" de cara al conductor) pero internamente llama a `rechazarDocumento`,
 * que es el único endpoint que existe para eso. Ver la nota en `Verificacion.tsx`.
 */
import { ErrorApi, obtener, postear } from './clienteApi';

// ---------------------------------------------------------------------------
// Enums reales — leídos de apps/api/prisma/schema.prisma, no adivinados.
// ---------------------------------------------------------------------------

export type EstadoVerificacionConductor =
  'PENDIENTE' | 'EN_REVISION' | 'APROBADO' | 'RECHAZADO' | 'SUSPENDIDO';

export type EstadoDisponibilidad = 'DESCONECTADO' | 'DISPONIBLE' | 'OCUPADO';

export type EstadoCuenta = 'ACTIVA' | 'SUSPENDIDA' | 'DADA_DE_BAJA';

export type TipoDocumento = 'LICENCIA_CONDUCIR' | 'SOAT' | 'TARJETA_PROPIEDAD' | 'REVISION_TECNICA';

export type EstadoRevisionDocumento = 'PENDIENTE' | 'APROBADO' | 'RECHAZADO';

export type FinalidadConsentimiento = 'OPERACION_VIAJE' | 'TRAFICO_AGREGADO';

export const ESTADOS_VERIFICACION: EstadoVerificacionConductor[] = [
  'PENDIENTE',
  'EN_REVISION',
  'APROBADO',
  'RECHAZADO',
  'SUSPENDIDO',
];

// ---------------------------------------------------------------------------
// GET /consola/verificacion/cola
// ---------------------------------------------------------------------------

/** `VerificacionService.cola()` — un renglón resumido por conductor. */
export interface ItemCola {
  conductorId: string;
  nombre: string | null;
  estadoVerificacion: EstadoVerificacionConductor;
  actualizadoAt: string;
  placa: string | null;
  documentosPendientes: number;
  documentosFaltantes: TipoDocumento[];
  documentosVencidos: TipoDocumento[];
  consentimientoOperacionVigente: boolean;
}

/**
 * `estado` por defecto `EN_REVISION` — la cadencia normal de un operador. El endpoint acepta
 * cualquiera de los 5 estados reales (no un conjunto: uno solo por llamada).
 */
export const obtenerCola = (
  estado: EstadoVerificacionConductor = 'EN_REVISION',
): Promise<ItemCola[]> => obtener<ItemCola[]>(`/consola/verificacion/cola?estado=${estado}`);

// ---------------------------------------------------------------------------
// GET /consola/verificacion/conductores/:id
// ---------------------------------------------------------------------------

/** `Documento` de Prisma, tal cual serializa por HTTP (fechas → ISO string). */
export interface DocumentoReal {
  id: string;
  conductorId: string;
  tipo: TipoDocumento;
  numero: string;
  vigenteHasta: string;
  /** Puntero opaco a MinIO. `null` = nunca se cargó nada. */
  archivoRef: string | null;
  estadoRevision: EstadoRevisionDocumento;
  revisadoPorId: string | null;
  revisadoAt: string | null;
  observacion: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface VehiculoReal {
  id: string;
  conductorId: string;
  placa: string;
  /** 📌 `MOTOTAXI` retirado el 2026-09-01; el enum de Prisma quedó con un solo valor. */
  tipo: 'AUTO';
  marca: string | null;
  modelo: string | null;
  anio: number | null;
  color: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface SuscripcionReal {
  id: string;
  conductorId: string;
  estado: string;
  origen: string;
  vigenteHasta: string | null;
  hubPagosSubId: string | null;
  hubPagosMerchantAppId: string | null;
  ultimaSyncAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface ConductorDetalle {
  id: string;
  estadoVerificacion: EstadoVerificacionConductor;
  estadoDisponibilidad: EstadoDisponibilidad;
  estadoCuenta: EstadoCuenta;
  /**
   * ⚠️ **STRING, no number.** `Prisma.Decimal` serializa a texto sobre HTTP — verificado por
   * efecto: `"4.75"`, no `4.75`. Formatear con cuidado (no hacer aritmética directa sin `Number()`).
   */
  reputacion: string | null;
  viajesTotales: number;
  ultimoViajeFinAt: string | null;
  createdAt: string;
  updatedAt: string;
  documentos: DocumentoReal[];
  vehiculo: VehiculoReal | null;
  suscripcion: SuscripcionReal | null;
  user: { nombre: string | null; email: string | null; phone: string | null };
}

export interface EstadoFinalidadConsentimiento {
  finalidad: FinalidadConsentimiento;
  vigente: boolean;
  versionAceptada: number | null;
  versionVigentePublicada: number | null;
  cubreTextoVigente: boolean;
  otorgadoAt: string | null;
  revocadoAt: string | null;
}

export interface DetalleVerificacion {
  conductor: ConductorDetalle;
  consentimientos: EstadoFinalidadConsentimiento[];
}

export const obtenerDetalle = (conductorId: string): Promise<DetalleVerificacion> =>
  obtener<DetalleVerificacion>(`/consola/verificacion/conductores/${conductorId}`);

// ---------------------------------------------------------------------------
// GET /consola/verificacion/documentos/:id/archivo
// ---------------------------------------------------------------------------

/**
 * URL firmada de vida corta. ⚠️ Medido por efecto: si `archivoRef` apunta a una clave que el
 * almacén no tiene (nunca se subió nada), el backend hoy responde **500**, no un 404 legible —
 * `ExpedienteService.urlArchivo` no envuelve el error del almacén. `Verificacion.tsx` lo trata
 * como "no se pudo abrir el archivo" en vez de mostrar el mensaje crudo del backend.
 */
export const obtenerUrlArchivo = async (
  documentoId: string,
): Promise<{ url: string; expiraEnSegundos: number }> => {
  try {
    return await obtener(`/consola/verificacion/documentos/${documentoId}/archivo`);
  } catch (error) {
    // El backend esconde el error real del almacén detrás de un 500 genérico (medido por
    // efecto). "Internal server error" no le dice nada al operador; esto sí.
    if (error instanceof ErrorApi && error.status >= 500) {
      throw new ErrorApi(
        error.status,
        'No se pudo abrir el archivo: el almacén de documentos no respondió. Probá de nuevo en un momento.',
      );
    }
    throw error;
  }
};

// ---------------------------------------------------------------------------
// POST .../documentos/:id/aprobar | rechazar
// ---------------------------------------------------------------------------

export interface ResultadoRevisionDocumento {
  id: string;
  estadoRevision: EstadoRevisionDocumento;
}

/** ⚠️ Rechaza con 400 si el documento ya está vencido (`vigenteHasta` en el pasado) — mensaje del backend. */
export const aprobarDocumento = (
  documentoId: string,
  observacion?: string,
): Promise<ResultadoRevisionDocumento> =>
  postear(`/consola/verificacion/documentos/${documentoId}/aprobar`, { observacion });

/** `motivo` obligatorio, mínimo 10 caracteres (`RechazarDto` — lo hace cumplir `DialogoMotivo`). */
export const rechazarDocumento = (
  documentoId: string,
  motivo: string,
): Promise<ResultadoRevisionDocumento> =>
  postear(`/consola/verificacion/documentos/${documentoId}/rechazar`, { motivo });

// ---------------------------------------------------------------------------
// POST .../conductores/:id/aprobar | rechazar
// ---------------------------------------------------------------------------

export interface ResultadoDecisionExpediente {
  conductorId: string;
  estadoVerificacion: EstadoVerificacionConductor;
}

/**
 * Aprobación del EXPEDIENTE completo. **Sin cuerpo** — el backend no lee `@Body()` en esta ruta.
 * Rechaza (400) si falta algún documento requerido, alguno está vencido/sin aprobar, o no hay
 * consentimiento `OPERACION_VIAJE` vigente — el mensaje del backend ya explica cuál.
 */
export const aprobarExpediente = (conductorId: string): Promise<ResultadoDecisionExpediente> =>
  postear(`/consola/verificacion/conductores/${conductorId}/aprobar`);

export const rechazarExpediente = (
  conductorId: string,
  motivo: string,
): Promise<ResultadoDecisionExpediente> =>
  postear(`/consola/verificacion/conductores/${conductorId}/rechazar`, { motivo });

// ---------------------------------------------------------------------------
// POST .../conductores/:id/consentimientos/revocar
// ---------------------------------------------------------------------------

/**
 * Revocación por OPERADOR (`docs/05` §6.4) — vía legítima (pedido telefónico, baja
 * administrativa). Rechaza con 404 si no hay un consentimiento VIGENTE de esa finalidad para
 * cerrar. Revocar `OPERACION_VIAJE` desconecta al conductor en la misma transacción (efecto del
 * backend, no de esta pantalla).
 */
export const revocarConsentimientoPorOperador = (
  conductorId: string,
  motivo: string,
  finalidad: FinalidadConsentimiento = 'OPERACION_VIAJE',
): Promise<{ finalidad: FinalidadConsentimiento; revocadoAt: string }> =>
  postear(`/consola/verificacion/conductores/${conductorId}/consentimientos/revocar`, {
    motivo,
    finalidad,
  });
